import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { login } from "../../sdk/auth/auth";
import { toast } from "react-toastify";
import { createPermissionApi } from "../../hooks/permissionUtils";
import { getFirstAllowedRoute } from "../../helpers/getFirstAllowedRoute";
import { menuItems } from "../../config/menuConfig";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const { setAuth } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // 🚨 Clean stale data BEFORE login
      const deviceId = localStorage.getItem("rehema_device_id");
      localStorage.clear();
      if (deviceId) {
        localStorage.setItem("rehema_device_id", deviceId);
      }

      const result = await login(username, password);
      console.log(result?.data);
      let user = result?.data?.user;
      let tokens = result?.data?.tokens;
      let roles = [1999, 2000, 2001];
      let role = result?.data;

      localStorage.setItem("accessToken", result?.data?.tokens?.accessToken);
      localStorage.setItem("refreshToken", result?.data?.tokens?.refreshToken);
      localStorage.setItem("userID", result?.data?.user?.id);

      setAuth({ user, roles, role, tokens });

      setIsLoading(false);

      if (tokens === null) {
        navigate("/verification", { replace: true });
      } else {
        const permissionApi = createPermissionApi(role?.userPermissions || []);

        const redirectTo = getFirstAllowedRoute(menuItems, permissionApi);

        navigate(redirectTo, { replace: true });
        toast?.success("Welcome to Sojrel");
      }
    } catch (error) {
      toast?.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="w-[100%] h-[calc(100vh)] bg-[#FFFFFF] relative">
        <div className="flex items-center h-full  relative">
          <div className="w-[90%] h-[95%] flex items-center px-5 gap-[80px]">
            <div className="w-[652px] bg-[#F0FFFE] relative h-[98%] flex flex-col items-center justify-center rounded-[30px]">
              <img src="/logo.png" alt="Logo" className="z-50" />

              <img
                src="/ground.png"
                alt="background"
                className="w-[80%] z-50 h-[50%]"
              />

              <div className="mt-16">
                <p className="text-[#005C53] text-[32px] font-[500]">
                  Making Your Work Easier To <br />
                  Serve Our Members Better.
                </p>

                <svg
                  width="472"
                  height="46"
                  viewBox="0 0 482 46"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 42C77.6804 19.2828 221.099 -3.53939 478 6.38599"
                    stroke="#005C53"
                    stroke-width="8"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
            </div>
            <div className="w-[50%] z-50 bg-white h-[98%] flex px-[20px] flex-col justify-center">
              <p className="text-[18px] text-[#005C53] mb-[20px]">
                Login to Sojrel
              </p>
              <form onSubmit={handleSubmit}>
                <div className="flex gap-[10px] flex-col mb-[30px]">
                  <label className="text-[#000]" htmlFor="username">
                    Username
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    className="border rounded-[5px] h-[50px] px-[20px] focus:outline-none"
                  />
                </div>
                <div className="flex gap-[10px] w-full flex-col">
                  <label className="text-[#000]" htmlFor="password">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="border w-full rounded-[5px] h-[50px] px-[20px] focus:outline-none"
                    />
                    <span
                      onClick={togglePasswordVisibility}
                      className="absolute top-[50%] right-[10px] transform translate-y-[-50%] cursor-pointer"
                    >
                      {showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                        >
                          <path
                            fill="currentColor"
                            d="M21.92 11.6C19.9 6.91 16.1 4 12 4s-7.9 2.91-9.92 7.6a1 1 0 0 0 0 .8C4.1 17.09 7.9 20 12 20s7.9-2.91 9.92-7.6a1 1 0 0 0 0-.8M12 18c-3.17 0-6.17-2.29-7.9-6C5.83 8.29 8.83 6 12 6s6.17 2.29 7.9 6c-1.73 3.71-4.73 6-7.9 6m0-10a4 4 0 1 0 4 4a4 4 0 0 0-4-4m0 6a2 2 0 1 1 2-2a2 2 0 0 1-2 2"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="22"
                          viewBox="0 0 32 32"
                        >
                          <path
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M21 16a5 5 0 0 1-5 5m-5-5a5 5 0 0 1 5-5m-3 13.654A13.38 13.38 0 0 0 16 25c7.18 0 13-6 13-9c0-1.336-1.155-3.268-3.071-5M19.5 7.47A13.49 13.49 0 0 0 16 7C8.82 7 3 13 3 16c0 1.32 1.127 3.22 3 4.935M7 25L25 7"
                          />
                        </svg>
                      )}
                    </span>
                  </div>
                </div>
                <button className="bg-[#005C53] mt-[30px] flex items-center justify-center gap-[10px] text-white rounded-[5px] w-[100%] h-[44px]">
                  {isLoading && (
                    <svg
                      aria-hidden="true"
                      className="w-[20px] h-[20px] text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  )}
                  Login
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
